import * as React from 'react';

import Imgix from 'js/components/Imgix';

import type { StyleDeclarationValue } from '@coursera/aphrodite';
import { Grid, Typography } from '@coursera/cds-core';
import { StyleSheet, breakPoint, css } from '@coursera/coursera-ui';

import { BASE_STYLES } from 'bundles/design-system/components/container-styles';
import { getDescriptionPageValueProps } from 'bundles/payments/data/getCourseraPlusValueProps';

import _t from 'i18n!nls/coursera-plus-landing-page';

const styles = StyleSheet.create({
  containerWrapper: {
    ...BASE_STYLES.containerWrapper,
  },
  container: {
    ...BASE_STYLES.container,
  },
  title: {
    ...BASE_STYLES.title,
    fontSize: '2.125rem !important',
    padding: '64px 0 16px 0',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      ...BASE_STYLES.titleMobile,
      padding: '48px 0 16px 0',
    },
  },
  subtitle: {
    fontSize: '16px',
    lineHeight: '24px',
    textAlign: 'center',
    color: '#1F1F1F',
    margin: '0 25px',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      fontSize: '16px',
      lineHeight: '24px',
    },
  },
  valuePropWrapper: {
    margin: '59px 0 63px 0 !important',
    justifyContent: 'space-between',
    [`@media (max-width: ${breakPoint.lg - 1}px)`]: {
      margin: '48px 0 0 0',
    },
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      flexDirection: 'column',
      margin: '34px 0 0 0',
      alignItems: 'center',
    },
  },
  valuePropCol: {
    ':first-child': {
      paddingLeft: '0',
    },
    ':last-child': {
      paddingRight: '0',
    },
  },
  valuePropImage: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '57px',
    margin: '0 0 16px 0',
  },
  valuePropTitle: {
    fontSize: '24px',
    lineHeight: '30px',
    textAlign: 'center',
    margin: '0 auto 8px',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      fontSize: '16px',
      lineHeight: '24px',
      fontWeight: 'bold',
    },
  },
  valuePropSubtitle: {
    lineHeight: '24px',
    textAlign: 'center',
    [`@media (max-width: ${breakPoint.lg - 1}px)`]: {
      marginBottom: '63px',
    },
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      marginBottom: '48px',
      maxWidth: '300px',
    },
    [`@media (max-width: ${breakPoint.sm - 1}px)`]: {
      fontSize: '14px',
      lineHeight: '24px',
    },
  },
});

const renderValueProps = ({ valuePropTitleStyles }: { valuePropTitleStyles?: StyleDeclarationValue }) => {
  return getDescriptionPageValueProps().map((valueProp) => {
    return (
      <Grid item key={valueProp.title} lg={3} md={3} xs className={css(styles.valuePropCol).className}>
        <div {...css(styles.valuePropImage)} aria-hidden="true">
          <Imgix alt={valueProp.title} src={valueProp.primaryImageUrl} maxHeight={valueProp.imageHeight} />
        </div>
        <Typography {...css(styles.valuePropTitle, valuePropTitleStyles)}>{valueProp.title}</Typography>
        <Typography {...css(styles.valuePropSubtitle)}>{valueProp.description}</Typography>
      </Grid>
    );
  });
};

type CourseraPlusValuePropsProps = {
  title?: React.ReactNode;
  subTitle?: React.ReactNode;
  valuePropTitleStyles?: StyleDeclarationValue;
};

const CourseraPlusValueProps = ({ title, subTitle, valuePropTitleStyles }: CourseraPlusValuePropsProps) => {
  let sectionTitle = _t('Invest in your career with Coursera Plus');
  let displaySubTitle = _t(
    'Get access to videos in over 90% of courses, Specializations, and Professional Certificates taught by top instructors from leading universities and companies.'
  );

  return (
    <Grid container {...css(styles.containerWrapper)} justifyContent="center">
      <Grid item className={css(styles.container).className}>
        <Typography variant="d2semibold" className={css(styles.title).className}>
          {title ?? sectionTitle}
        </Typography>
        <Grid container justifyContent="center">
          <Grid sm={10} md={8}>
            <Typography {...css(styles.subtitle)}>{subTitle ?? displaySubTitle}</Typography>
          </Grid>
          <Grid container spacing={24} className={css(styles.valuePropWrapper).className}>
            {renderValueProps({ valuePropTitleStyles })}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default CourseraPlusValueProps;
